import { useEffect, useMemo, useState, ChangeEvent } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { Feature, PlanInterval } from '../../models';
import { formatCurrency } from '../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../utils/Constants';
import { PlanBillingSwitch } from '../plan/component/true-platform/PlanBillingSwitch';
import { ProductIconBadge } from '../plan/component/true-platform/trueTestingProducts';
import { getSummaryUnitWord } from '../plan/component/true-platform/summaryUnits';
import {
  ADD_PRODUCTS_MAX_QUANTITY,
  AddProductsRow,
  getAddProductsLinePrice,
} from './addProductsRows';

export interface AddProductsSelection {
  quantity: number;
  interval: PlanInterval;
}

/**
 * The dialog's result keyed by feature: the new state of every listed product, quantity 0 for
 * one taken out of the order.
 */
export type AddProductsResult = Partial<Record<Feature, AddProductsSelection>>;

const GRID_COLUMNS = 'minmax(0, 1fr) 130px 140px 190px';

const useStyles = makeStyles(theme => ({
  paper: {
    width: '100%',
    maxWidth: 920,
    maxHeight: 'calc(100vh - 48px)',
    margin: 24,
    borderRadius: 20,
    border: '1px solid #e1e7e4',
    boxShadow: 'none',
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    '& .MuiTypography-root, & button, & input, & select': {
      fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: '24px 28px 16px',
  },
  title: {
    '&&': {
      fontFamily: "'Inter Tight', sans-serif",
    },
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: '-.01em',
    lineHeight: 1.3,
    color: '#16141d',
    outline: 'none',
  },
  subtitle: {
    marginTop: 2,
    fontSize: 14,
    lineHeight: 1.45,
    color: '#5b6661',
  },
  closeButton: {
    flex: 'none',
    width: 40,
    height: 40,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    borderRadius: 10,
    background: 'transparent',
    color: '#5b6661',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#eef2f0',
    },
  },
  body: {
    overflow: 'auto',
    padding: '0 32px 8px 28px',
  },
  table: {
    minWidth: 640,
  },
  headRow: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    columnGap: theme.spacing(2),
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #eef2f0',
    fontSize: 12,
    color: '#5b6661',
  },
  headCellEnd: {
    justifySelf: 'end',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: GRID_COLUMNS,
    columnGap: theme.spacing(2),
    alignItems: 'center',
    minHeight: 72,
    boxSizing: 'border-box',
    borderBottom: '1px solid #eef2f0',
  },
  product: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  productText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  productName: {
    fontSize: 14,
    color: '#16141d',
  },
  productNameRemoved: {
    color: '#5b6661',
    textDecoration: 'line-through',
  },
  removedNote: {
    display: 'block',
    fontSize: 12,
    lineHeight: 1.5,
    color: '#5b6661',
    whiteSpace: 'nowrap',
  },
  undoButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    fontWeight: 600,
    color: '#0b6b4f',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  addButton: {
    height: 40,
    padding: '0 18px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #cdd6d2',
    borderRadius: 10,
    backgroundColor: '#ffffff',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f3f5f4',
    },
    '&:disabled': {
      color: '#7f8b86',
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
    },
  },
  stepper: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 40,
    border: '1px solid #cdd6d2',
    borderRadius: 10,
    overflow: 'hidden',
  },
  stepperButton: {
    width: 38,
    height: '100%',
    border: 'none',
    backgroundColor: '#ffffff',
    font: 'inherit',
    fontSize: 16,
    color: '#16141d',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f3f5f4',
    },
    '&:disabled': {
      color: '#7f8b86',
      cursor: 'default',
      '&:hover': {
        backgroundColor: '#ffffff',
      },
    },
  },
  stepperInput: {
    width: 44,
    height: '100%',
    border: 'none',
    borderLeft: '1px solid #e1e7e4',
    borderRight: '1px solid #e1e7e4',
    textAlign: 'center',
    font: 'inherit',
    fontSize: 14,
    color: '#16141d',
    outline: 'none',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    MozAppearance: 'textfield',
  },
  price: {
    textAlign: 'right',
    lineHeight: 1.35,
  },
  priceAmount: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  priceAmountMuted: {
    color: '#5b6661',
  },
  priceUnit: {
    fontSize: 12,
    color: '#5b6661',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    padding: '16px 28px 24px',
    borderTop: '1px solid #eef2f0',
  },
  footerSummary: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  cancelButton: {
    whiteSpace: 'nowrap',
    height: 44,
    padding: '0 20px',
    border: '1px solid #cdd6d2',
    borderRadius: 10,
    backgroundColor: '#ffffff',
    font: 'inherit',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f3f5f4',
    },
  },
  saveButton: {
    whiteSpace: 'nowrap',
    height: 44,
    padding: '0 22px',
    border: 'none',
    borderRadius: 10,
    font: 'inherit',
    fontSize: 15,
    fontWeight: 500,
    backgroundColor: '#0f8461',
    color: '#ffffff',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#e1e7e4',
      color: '#5b6661',
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: '#e1e7e4',
      },
    },
  },
}));

const clampQuantity = (value: number, min = 0): number => {
  if (!Number.isFinite(value) || value < min) {
    return min;
  }
  return Math.min(ADD_PRODUCTS_MAX_QUANTITY, Math.floor(value));
};

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

type Draft = Partial<Record<Feature, AddProductsSelection>>;

const buildInitialDraft = (rows: AddProductsRow[]): Draft => Object.fromEntries(rows.map(row => [
  row.feature,
  { quantity: row.inOrderQuantity, interval: row.interval },
]));

/**
 * "Add more products" of the checkout Order Summary (design 6.10, `orderEditFlow = Add more
 * products`): the add-ons added on the checkout page come first with their quantity and cycle
 * editable (0 = "Will be removed", with Undo), the rest of the catalogue can be added. The
 * products chosen on the plan page are not listed - the caller leaves them out of [rows].
 * "Update order" returns the new state of every listed product.
 */
export const AddProductsDialog = (
  {
    open,
    rows,
    onClose,
    onSave,
  }: {
    open: boolean;
    rows: AddProductsRow[];
    onClose: () => void;
    onSave: (result: AddProductsResult) => void;
  },
) => {
  const classes = useStyles();
  const intl = useIntl();
  const [draft, setDraft] = useState<Draft>(() => buildInitialDraft(rows));

  // A fresh draft every time the dialog opens: the order as it is.
  useEffect(() => {
    if (open) {
      setDraft(buildInitialDraft(rows));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getSelection = (row: AddProductsRow): AddProductsSelection => draft[row.feature]
    ?? { quantity: row.inOrderQuantity, interval: row.interval };
  const updateSelection = (
    feature: Feature,
    update: (current: AddProductsSelection) => AddProductsSelection,
  ) => setDraft(current => {
    const row = rows.find(candidate => candidate.feature === feature);
    const selection = current[feature] ?? {
      quantity: row?.inOrderQuantity ?? 0,
      interval: row?.interval ?? PlanInterval.YEAR,
    };
    return { ...current, [feature]: update(selection) };
  });

  const inOrderRows = rows.filter(row => row.inOrderQuantity > 0);
  const otherRows = rows.filter(row => row.inOrderQuantity === 0);
  // Design: the in-order products first, annual lines before monthly ones.
  const orderedRows = [
    ...inOrderRows.filter(row => row.interval === PlanInterval.YEAR),
    ...inOrderRows.filter(row => row.interval !== PlanInterval.YEAR),
    ...otherRows,
  ];

  const { changed, deltaAnnual, deltaMonthly } = useMemo(() => {
    let annual = 0;
    let monthly = 0;
    let anyChange = false;
    rows.forEach(row => {
      const selection = getSelection(row);
      const before = getAddProductsLinePrice(row, row.inOrderQuantity, row.interval);
      const after = getAddProductsLinePrice(row, selection.quantity, selection.interval);
      if (row.interval === PlanInterval.YEAR) annual -= before;
      else monthly -= before;
      if (selection.interval === PlanInterval.YEAR) annual += after;
      else monthly += after;
      if (selection.quantity !== row.inOrderQuantity
        || (selection.quantity > 0 && selection.interval !== row.interval)) {
        anyChange = true;
      }
    });
    return { changed: anyChange, deltaAnnual: annual, deltaMonthly: monthly };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, rows]);

  const formatAmount = (amount: number) => formatCurrency(
    intl,
    Math.abs(amount),
    DEFAULT_CURRENCY_FORMAT_OPTIONS,
  );
  const signed = (amount: number, suffix: string) => (
    `${amount > 0 ? '+ ' : '− '}${formatAmount(amount)}${suffix}`
  );
  const yearSuffix = intl.formatMessage({ id: 'page.plan.v2.true.testing.price.year' });
  const monthSuffix = intl.formatMessage({ id: 'page.plan.v2.true.testing.price.month' });
  const deltaParts = [
    deltaAnnual !== 0 ? signed(deltaAnnual, yearSuffix) : '',
    deltaMonthly !== 0 ? signed(deltaMonthly, monthSuffix) : '',
  ].filter(Boolean);

  let summary: string;
  if (!changed) {
    summary = intl.formatMessage({ id: 'page.order.confirmation.v2.products.no.changes' });
  } else {
    summary = deltaParts.length > 0
      ? deltaParts.join(' · ')
      : intl.formatMessage({ id: 'page.order.confirmation.v2.products.no.price.change' });
  }

  const handleSave = () => {
    if (!changed) {
      return;
    }
    const result: AddProductsResult = {};
    rows.forEach(row => {
      result[row.feature] = getSelection(row);
    });
    onSave(result);
  };

  // The same Annual / Monthly switch as the plan page (issue #1274, #4: the dialog's native
  // select looked different from the selector outside it). It carries its own locked tooltip.
  const renderCycleSelect = (row: AddProductsRow, selection: AddProductsSelection) => (
    <PlanBillingSwitch
      id={`add-products-cycle-${row.feature.toLowerCase()}`}
      interval={selection.interval}
      disabled={row.intervalLocked}
      ariaLabel={intl.formatMessage(
        { id: 'page.order.confirmation.v2.products.cycle.aria' },
        { product: intl.formatMessage({ id: row.titleId }) },
      )}
      onChange={interval => updateSelection(row.feature, current => ({ ...current, interval }))}
    />
  );

  const renderQuantity = (row: AddProductsRow, selection: AddProductsSelection) => {
    const name = intl.formatMessage({ id: row.titleId });
    const unitWord = getSummaryUnitWord(row.feature, 1);
    const showStepper = row.inOrderQuantity > 0 || selection.quantity > 0;
    if (!showStepper) {
      const addButton = (
        <button
          type="button"
          id={`add-products-add-${row.feature.toLowerCase()}`}
          className={classes.addButton}
          disabled={!!row.blockedHint}
          aria-label={intl.formatMessage(
            { id: 'page.order.confirmation.v2.products.add.aria' },
            { product: name },
          )}
          onClick={() => updateSelection(row.feature, current => ({ ...current, quantity: 1 }))}
        >
          +
          {' '}
          <FormattedMessage id="page.plan.v2.true.bundle.add" />
        </button>
      );
      return row.blockedHint ? (
        <Tooltip disableInteractive placement="top" title={row.blockedHint}>
          <span style={{ display: 'inline-flex' }}>{addButton}</span>
        </Tooltip>
      ) : addButton;
    }
    return (
      <span className={classes.stepper}>
        <button
          type="button"
          className={classes.stepperButton}
          disabled={selection.quantity <= 0}
          aria-label={intl.formatMessage(
            { id: 'page.order.confirmation.v2.products.decrease.aria' },
            { product: name, unit: unitWord },
          )}
          onClick={() => updateSelection(row.feature, current => ({
            ...current,
            quantity: clampQuantity(current.quantity - 1),
          }))}
        >
          &minus;
        </button>
        <input
          id={`add-products-quantity-${row.feature.toLowerCase()}`}
          className={classes.stepperInput}
          type="number"
          inputMode="numeric"
          min={0}
          max={ADD_PRODUCTS_MAX_QUANTITY}
          value={selection.quantity}
          aria-label={intl.formatMessage(
            { id: 'page.order.confirmation.v2.products.quantity.aria' },
            { product: name },
          )}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const quantity = clampQuantity(Number.parseInt(event.target.value, 10));
            updateSelection(row.feature, current => ({ ...current, quantity }));
          }}
        />
        <button
          type="button"
          className={classes.stepperButton}
          disabled={selection.quantity >= ADD_PRODUCTS_MAX_QUANTITY}
          aria-label={intl.formatMessage(
            { id: 'page.order.confirmation.v2.products.increase.aria' },
            { product: name, unit: unitWord },
          )}
          onClick={() => updateSelection(row.feature, current => ({
            ...current,
            quantity: clampQuantity(current.quantity + 1),
          }))}
        >
          +
        </button>
      </span>
    );
  };

  const renderPrice = (row: AddProductsRow, selection: AddProductsSelection, muted: boolean) => {
    const isAnnual = selection.interval === PlanInterval.YEAR;
    const amount = isAnnual ? row.unitPrices.YEAR : row.unitPrices.MONTH;
    const unitLine = intl.formatMessage(
      {
        id: isAnnual
          ? 'page.plan.v2.true.testing.price.unit.year'
          : 'page.plan.v2.true.testing.price.unit.month',
      },
      { unit: row.unit },
    );
    return (
      <Box className={classes.price}>
        <Typography className={clsx(classes.priceAmount, muted && classes.priceAmountMuted)}>
          {formatCurrency(intl, amount, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
        </Typography>
        <Typography className={classes.priceUnit}>{unitLine}</Typography>
        {isAnnual && row.tier && (
          <Typography className={classes.priceUnit}>
            <FormattedMessage
              id="page.order.confirmation.v2.products.tier.note"
              values={{
                count: row.tier.count,
                price: formatCurrency(intl, row.tier.thenPrice, DEFAULT_CURRENCY_FORMAT_OPTIONS),
              }}
            />
          </Typography>
        )}
      </Box>
    );
  };

  const renderRow = (row: AddProductsRow) => {
    const selection = getSelection(row);
    const willRemove = row.inOrderQuantity > 0 && selection.quantity === 0;
    return (
      <Box
        key={row.feature}
        id={`add-products-row-${row.feature.toLowerCase()}`}
        className={classes.row}
      >
        <Box className={classes.product}>
          <ProductIconBadge iconKey={row.iconKey} size={20} />
          <Box className={classes.productText}>
            <Typography
              component="span"
              className={clsx(classes.productName, willRemove && classes.productNameRemoved)}
            >
              <FormattedMessage id={row.titleId} />
            </Typography>
            {willRemove && (
              <span className={classes.removedNote}>
                <FormattedMessage id="page.order.confirmation.v2.products.will.be.removed" />
                {' · '}
                <button
                  type="button"
                  className={classes.undoButton}
                  onClick={() => updateSelection(row.feature, current => ({
                    ...current,
                    quantity: row.inOrderQuantity,
                  }))}
                >
                  <FormattedMessage id="page.order.confirmation.v2.products.undo" />
                </button>
              </span>
            )}
          </Box>
        </Box>
        <Box>{renderCycleSelect(row, selection)}</Box>
        <Box>{renderQuantity(row, selection)}</Box>
        {renderPrice(row, selection, willRemove)}
      </Box>
    );
  };

  const headRow = (
    <Box className={classes.headRow}>
      <span><FormattedMessage id="page.plan.v2.table.header.product" /></span>
      <span><FormattedMessage id="page.plan.v2.billing.cycle" /></span>
      <span><FormattedMessage id="page.plan.v2.table.header.quantity" /></span>
      <span className={classes.headCellEnd}>
        <FormattedMessage id="page.plan.v2.table.header.price.short" />
      </span>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      id="add-products-dialog"
      aria-labelledby="add-products-dialog-title"
      maxWidth={false}
      classes={{ paper: classes.paper }}
      BackdropProps={{ style: { backgroundColor: 'rgba(15, 20, 25, .4)' } }}
    >
      <Box className={classes.header}>
        <Box>
          <Typography id="add-products-dialog-title" component="h2" className={classes.title}>
            <FormattedMessage id="page.order.confirmation.v2.add.products.title" />
          </Typography>
          <Typography className={classes.subtitle}>
            <FormattedMessage id="page.order.confirmation.v2.add.products.subtitle" />
          </Typography>
        </Box>
        <button
          type="button"
          id="add-products-dialog-close"
          className={classes.closeButton}
          aria-label={intl.formatMessage({ id: 'common.close' })}
          onClick={onClose}
        >
          <CloseIcon />
        </button>
      </Box>

      <Box className={classes.body}>
        <Box className={classes.table}>
          {headRow}
          {orderedRows.map(renderRow)}
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Typography
          id="add-products-dialog-summary"
          component="span"
          aria-live="polite"
          className={classes.footerSummary}
        >
          {summary}
        </Typography>
        <Box className={classes.footerActions}>
          <button
            type="button"
            id="add-products-dialog-cancel"
            className={classes.cancelButton}
            onClick={onClose}
          >
            <FormattedMessage id="common.cancel" />
          </button>
          <button
            type="button"
            id="add-products-dialog-save"
            className={classes.saveButton}
            disabled={!changed}
            onClick={handleSave}
          >
            <FormattedMessage id="page.order.confirmation.v2.products.update.order" />
          </button>
        </Box>
      </Box>
    </Dialog>
  );
};
